import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Send, X } from 'lucide-react';
import UserModalSelect from './UserModalSelect';
import RnUzurkuvanjeThreadCard from './RnUzurkuvanjeThreadCard';
import DateCurrentInput from '../inputs/DateCurrentInput';
import { useRnUzurkuvanjeRequests } from '../hooks/useRnUzurkuvanjeRequests';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import type { RnUzurkuvanjeReleasePayload } from '../types/rnUzurkuvanjeRequests';
import type { RNMaterialPriemRow } from './RNMaterialPriemTable';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApprovalChange?: () => void;
  rnWorkOrderId: string;
  rnWorkOrderNumber?: string;
  rows: RNMaterialPriemRow[];
}

const parseDDMMYYYYToISO = (value: string): string | undefined => {
  const parsed = moment(value.trim(), 'DD.MM.YYYY', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

const RnUzurkuvanjeRequestModal: React.FC<Props> = ({ isOpen, onClose, onApprovalChange, rnWorkOrderId, rnWorkOrderNumber, rows }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user } = useAuth();
  const {
    threads,
    loading,
    nextNumber,
    sampledSubprocessIds,
    viewerCanApproveAsQc,
    intermediateRequiresQcRelease,
    viewerCanReleaseAsQc,
    loadThreads,
    createThread,
    addMessage,
    closeThread,
    approveThread,
    releaseThread,
    refreshNextNumber,
  } = useRnUzurkuvanjeRequests();
  const [recipients, setRecipients] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState<Record<string, string>>({});
  const [pinText, setPinText] = useState<Record<string, string>>({});
  const [replyDateMode, setReplyDateMode] = useState<Record<string, 'accept' | 'new'>>({});
  const [replyDateValue, setReplyDateValue] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen && rnWorkOrderId) {
      void loadThreads({ rnWorkOrder: rnWorkOrderId });
      void refreshNextNumber(rnWorkOrderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, rnWorkOrderId]);

  if (!isOpen) return null;

  const recipientIds = Array.from(new Set(recipients.split(',').map((id) => id.trim()).filter(Boolean)));
  const sampledRows = rows.filter((row) => (row.uzurkuvanje || '').trim() !== '');
  const canSubmit = recipientIds.length > 0 && !submitting && !!rnWorkOrderId;
  const displayNumber = nextNumber || '';
  // Each row: ident — name — planned — received (what was actually weighed in Прием),
  // so QC sees how much came in next to what was planned.
  const requestText = rows.map((row, index) => [row.ident, row.name, row.quantity, (row.measuredQty || '').trim() ? `${t('rnUzurkuvanjeRequests.receivedQuantity')}: ${String(row.measuredQty).trim()}` : ''].filter(Boolean).join(' — ') || `${t('rnUzurkuvanjeRequests.row')} ${index + 1}`).join('\n') || t('rnUzurkuvanjeRequests.noMaterialRows');

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (sampledRows.length === 0) {
      showToast(t('rnUzurkuvanjeRequests.noSampledRows'), 'error');
      return;
    }
    if (sampledRows.some((row) => !row.subprocessId || !row.rowId)) {
      showToast(t('toasts.genericError'), 'error');
      return;
    }
    setSubmitting(true);
    const created = await createThread({
      rnWorkOrder: rnWorkOrderId,
      subject: `Узоркување — ${rnWorkOrderNumber || displayNumber || rnWorkOrderId}`,
      priority: 'medium',
      recipients: recipientIds,
      rows: sampledRows.map((row) => ({ subprocessId: row.subprocessId || '', rowKey: row.rowId || '' })),
      text: requestText,
      dateTo: parseDDMMYYYYToISO(dateTo) || null,
    });
    setSubmitting(false);
    if (created) {
      setRecipients('');
      setDateTo('');
      const numberLabel = created.number ? ` (${created.number})` : '';
      showToast(`${t('rnUzurkuvanjeRequests.sent') || 'Uzorkuvanje sent'}${numberLabel}`, 'success');
      void refreshNextNumber(rnWorkOrderId);
    }
  };

  const handleReply = async (threadId: string) => {
    const value = (replyText[threadId] || '').trim();
    if (!value) return;
    const mode = replyDateMode[threadId];
    const newDateIso = mode === 'new' ? parseDDMMYYYYToISO(replyDateValue[threadId] || '') : undefined;
    const updated = await addMessage(threadId, value, newDateIso);
    if (updated) {
      setReplyText((prev) => ({ ...prev, [threadId]: '' }));
      setReplyDateMode((prev) => ({ ...prev, [threadId]: 'accept' }));
      setReplyDateValue((prev) => ({ ...prev, [threadId]: '' }));
    }
  };

  const handleClose = async (threadId: string) => {
    const updated = await closeThread(threadId);
    if (updated) showToast(t('rnUzurkuvanjeRequests.closedSuccess') || 'Closed', 'success');
  };

  const handleApprove = async (threadId: string) => {
    const value = (replyText[threadId] || '').trim();
    const pin = (pinText[threadId] || '').trim();
    if (!pin) return;
    const updated = await approveThread(threadId, { pin, text: value || undefined });
    if (updated) {
      setReplyText((prev) => ({ ...prev, [threadId]: '' }));
      setPinText((prev) => ({ ...prev, [threadId]: '' }));
      onApprovalChange?.();
    }
  };

  const handleRelease = async (threadId: string, payload: RnUzurkuvanjeReleasePayload) => {
    const updated = await releaseThread(threadId, payload);
    if (updated) onApprovalChange?.();
    return !!updated;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="min-w-0 pr-3">
            <div className="text-sm font-semibold text-blue-700">
              {displayNumber || t('rnUzurkuvanjeRequests.title')}
            </div>
            <h2 className="truncate text-xs font-medium text-gray-600">
              {t('rnUzurkuvanjeRequests.title')}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50" aria-label={t('rnUzurkuvanjeRequests.close')}>
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4">
          <div className="space-y-3 rounded-md border border-gray-200 p-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">{t('rnUzurkuvanjeRequests.workOrderNo')}</label>
              <div className="flex h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
                {rnWorkOrderNumber || '—'}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">{t('rnUzurkuvanjeRequests.requestNumber')}</label>
              <div className="flex h-9 items-center rounded-md border border-blue-200 bg-blue-50 px-3 text-sm font-semibold text-blue-800">
                {displayNumber || '—'}
              </div>
            </div>
            <div className="max-w-xs">
              <label className="mb-1 block text-xs font-semibold text-gray-700">{t('rnUzurkuvanjeRequests.dueDate')}</label>
              <DateCurrentInput
                value={dateTo}
                onChange={setDateTo}
                showIcon
                inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-800 bg-white focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-gray-700">{t('rnUzurkuvanjeRequests.recipients')} <span className="text-red-500">*</span></label>
              <UserModalSelect value={recipients} onChange={setRecipients} placeholder={t('rnUzurkuvanjeRequests.recipients') || undefined} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] table-fixed border-collapse">
                <thead><tr>
                  {['subprocess', 'ident', 'name', 'unit', 'plannedQuantity', 'receivedQuantity'].map((key) => <th key={key} className="h-6 border border-gray-300 bg-gray-200 px-1 text-center text-[10px] font-semibold text-gray-700">{t(`rnUzurkuvanjeRequests.${key}`)}</th>)}
                </tr></thead>
                <tbody>{rows.length === 0 ? <tr><td colSpan={6} className="h-[26px] border border-gray-300 px-1.5 py-0.5 text-[11px] italic text-gray-400">{t('rnUzurkuvanjeRequests.noMaterialRows')}</td></tr> : rows.map((row, index) => (
                  <tr key={row.rowId || index}>
                    <td className="h-[26px] border border-gray-300 px-1.5 py-0.5 text-center text-[11px] text-gray-700">{row.subprocessName}</td>
                    <td className="h-[26px] border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-700">{row.ident}</td>
                    <td className="h-[26px] truncate border border-gray-300 px-1.5 py-0.5 text-[11px] text-gray-700" title={row.name}>{row.name}</td>
                    <td className="h-[26px] border border-gray-300 px-1.5 py-0.5 text-center text-[11px] text-gray-700">{row.unitCode}</td>
                    <td className="h-[26px] border border-gray-300 px-1.5 py-0.5 text-right text-[11px] text-gray-700">{row.quantity}</td>
                    <td className="h-[26px] border border-gray-300 px-1.5 py-0.5 text-right text-[11px] font-semibold text-gray-800">{(row.measuredQty || '').trim() || '—'}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={handleSubmit} disabled={!canSubmit} className={`inline-flex h-9 items-center gap-2 rounded-md px-4 text-xs font-semibold transition-colors ${canSubmit ? 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/50' : 'cursor-not-allowed bg-gray-200 text-gray-400'}`}>
                <Send size={15} />{t('rnUzurkuvanjeRequests.send')}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {loading && <div className="text-center text-xs text-gray-500">{t('common.loading') || 'Loading...'}</div>}
            {!loading && threads.length === 0 && (
              <div className="py-6 text-center text-xs text-gray-500">{t('rnUzurkuvanjeRequests.noThreads')}</div>
            )}
            {threads.map((thread) => (
              <RnUzurkuvanjeThreadCard
                key={thread._id}
                thread={thread}
                currentUserId={user?._id}
                viewerCanApproveAsQc={viewerCanApproveAsQc}
                intermediateRequiresQcRelease={intermediateRequiresQcRelease}
                viewerCanReleaseAsQc={viewerCanReleaseAsQc}
                onRelease={(payload) => handleRelease(thread._id, payload)}
                sampledSubprocessIds={sampledSubprocessIds}
                pin={pinText[thread._id] || ''}
                onPinChange={(value) => setPinText((prev) => ({ ...prev, [thread._id]: value }))}
                replyValue={replyText[thread._id] || ''}
                onReplyChange={(value) => setReplyText((prev) => ({ ...prev, [thread._id]: value }))}
                replyDateMode={replyDateMode[thread._id] || 'accept'}
                replyDateValue={replyDateValue[thread._id] || ''}
                onReplyDateModeChange={(value) => setReplyDateMode((prev) => ({ ...prev, [thread._id]: value }))}
                onReplyDateValueChange={(value) => setReplyDateValue((prev) => ({ ...prev, [thread._id]: value }))}
                onReply={() => void handleReply(thread._id)}
                onClose={() => void handleClose(thread._id)}
                onApprove={() => void handleApprove(thread._id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RnUzurkuvanjeRequestModal;
